import {connect} from 'react-redux';
import {Badge, Col, Container, Row} from 'reactstrap';
import Sidebar from './components/sidebar';
import {IRootState} from 'app/shared/reducers';
import {getEntities as getJourneys} from '../journey/journey.reducer';
import React, {useEffect, useState} from 'react';
import {useLocation} from 'react-router-dom';
import JourneyInfo from './components/journey-info';
import {underscoreToTitleCase} from "app/shared/util/text-util";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {IPatientCase} from "app/shared/model/patient-case.model";
import {IJourney} from "app/shared/model/journey.model";
import {convertDateTimeFromServer} from "app/shared/util/date-utils";

interface IJourneyDetailsProps extends StateProps, DispatchProps {
}

// Symptoms section component
const SymptomsSection = ({patientCase, isExpanded, onToggle}) => {
  return (
    <>
      <p className="text-secondary" onClick={onToggle} style={{cursor: 'pointer'}}>
        <strong>Symptoms and Follow-Up Details&nbsp;</strong>
        <FontAwesomeIcon icon={isExpanded ? 'caret-up' : 'caret-down'}/>
      </p>
      {isExpanded && (
        <div className="mt-2 mb-3 ml-3">
          {patientCase?.motherSymptoms && patientCase.motherSymptoms.length > 0 && (
            <>
              <h6>Mother Symptoms:</h6>
              <ul>
                {patientCase.motherSymptoms
                  .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                  .map((symptom, i) => (
                    <li key={`mother-symptom-${i}`}>{symptom.enCondition || 'N/A'}</li>
                  ))}
                {patientCase.motherSymptom && (
                  <li>Additional Notes - {patientCase.motherSymptom}</li>
                )}
              </ul>
            </>
          )}
          {patientCase?.babySymptoms && patientCase.babySymptoms.length > 0 && (
            <>
              <h6>Baby Symptoms:</h6>
              <ul>
                {patientCase.babySymptoms
                  .sort((a, b) => (a.displayOrder || 0) - (b.displayOrder || 0))
                  .map((symptom, i) => (
                    <li key={`baby-symptom-${i}`}>{symptom.enCondition || 'N/A'}</li>
                  ))}
                {patientCase.babySymptom && (
                  <li>Additional Notes - {patientCase.babySymptom}</li>
                )}
              </ul>
            </>
          )}
          {(!patientCase?.motherSymptoms || patientCase.motherSymptoms.length === 0) &&
            (!patientCase?.babySymptoms || patientCase.babySymptoms.length === 0) && (
              <p>No symptoms recorded.</p>
            )}
        </div>
      )}
    </>
  );
};

const JourneyDetails = (props: IJourneyDetailsProps) => {
  const {
    journeyEntities,
    loading,
    journeyErrorMessage,
    followupErrorMessage,
    updatingSuccess,
    paymentCreateSuccess,
    followupSaveSuccess,
    patientFeedbackUpdateSuccess
  } = props;
  const [patientCase, setPatientCase] = React.useState<IPatientCase | null>(null);
  const [latestJourney, setLatestJourney] = useState<IJourney | null>(null);
  const [isSymptomsExpanded, setIsSymptomsExpanded] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const patientCaseId = queryParams.get('patientCaseId');
  const toggleSymptoms = () => setIsSymptomsExpanded(!isSymptomsExpanded);

  useEffect(() => {
    if (patientCaseId) {
      props.getJourneys(false, undefined, patientCaseId);
    }
  }, [patientCaseId]);

  useEffect(() => {
    if (journeyEntities.length > 0) {
      // Set the patient case from the last journey entity
      const lastJourney = [...journeyEntities].sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))[0];
      setPatientCase(lastJourney.patientCase);
      setLatestJourney(lastJourney);

      // scroll to the top of the page when new journeys are loaded
      window.scrollTo(0, 0);
    }
  }, journeyEntities);

  // On error alert the user
  useEffect(() => {
    if (journeyErrorMessage || followupErrorMessage) {
      const message = journeyErrorMessage || followupErrorMessage;
      alert(`Error updating journey: ${message}`);
    }
  }, [journeyErrorMessage, followupErrorMessage]);

  // When the journey or payment update successfully, close modals and reload the page to reflect changes
  useEffect(() => {
    if (updatingSuccess || paymentCreateSuccess || followupSaveSuccess || patientFeedbackUpdateSuccess) {
      window.location.reload();
    }
  }, [updatingSuccess, paymentCreateSuccess, followupSaveSuccess, patientFeedbackUpdateSuccess]);


  return (
    <Container fluid>
      <Row>
        <Col md={3} className="bg-light">
          <Sidebar/>
        </Col>
        <Col md={9} className="p-4 position-relative">
          {loading ? (
            <div className="text-center py-5">
              <div className="mt-3">Loading...</div>
            </div>
          ) : (
            <>
              <div className="border-bottom pb-3 mb-3">
                <h6 className="text-muted">{patientCase?.type} ({patientCase?.emergencyType})</h6>
                <h2>
                  {patientCase?.patient?.lastName}, <span
                  className="text-primary">{patientCase?.patient?.firstName}</span>
                </h2>
                <br/>
                <p>
                  <strong>Primary Contact
                    ({patientCase?.primaryContactType}):</strong> {patientCase?.primaryContactNumber || '-'}
                </p>
                <p>
                  <strong>Secondary Contact:</strong> {patientCase?.secondaryContactNumber || '-'}
                </p>

                <SymptomsSection
                  patientCase={patientCase}
                  isExpanded={isSymptomsExpanded}
                  onToggle={toggleSymptoms}
                />
              </div>
              <div className="status-badges">
                <Badge color="primary" className="dispatcher-badge">
                  <FontAwesomeIcon icon="user" className="mr-2"/> {patientCase?.caseStatus}
                </Badge>
                <Badge color="info" className="dispatcher-badge">
                  <FontAwesomeIcon icon="bus" className="mr-2"/> {underscoreToTitleCase(latestJourney?.status || '')}
                </Badge>
                <Badge color="secondary" className="dispatcher-badge">
                  <FontAwesomeIcon
                    icon="calendar-alt"
                    className="mr-2"/> {latestJourney?.createdAt ? convertDateTimeFromServer(latestJourney?.createdAt) : ''}
                </Badge>
              </div>
              {journeyEntities?.length > 0 ? (
                [...journeyEntities]
                  .sort((a, b) => (a.createdAt > b.createdAt ? -1 : 1))
                  .map((journeyEntity: IJourney, i: number) => (
                    <JourneyInfo key={journeyEntity.id} journeyEntity={journeyEntity} index={journeyEntities.length - i}
                                 totalJourneys={journeyEntities.length}/>
                  ))
              ) : (
                <div className="text-muted text-center py-4">No journey found</div>
              )}
            </>
          )}
        </Col>
      </Row>
    </Container>
  );
};

const mapStateToProps = (storeState: IRootState) => ({
  journeyEntities: storeState.journey.entities,
  loading: storeState.journey.loading,
  journeyErrorMessage: storeState.journey.errorMessage,
  followupErrorMessage: storeState.patientCase.errorMessage,
  updatingSuccess: storeState.journey.updateSuccess,
  paymentCreateSuccess: storeState.payment.updateSuccess,
  followupSaveSuccess: storeState.patientCase.followupSaveSuccess,
  patientFeedbackUpdateSuccess: storeState.patientFeedback.updateSuccess
});

const mapDispatchToProps = {
  getJourneys,
};

type StateProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = typeof mapDispatchToProps;

export default connect(mapStateToProps, mapDispatchToProps)(JourneyDetails);
